export type SlotFilenameSlotInfo = {
  deviceFilename?: string;
  programTitle?: string;
};

const EMPTY_SLOT_FILENAME = 'empty.py';
const SAFE_PYTHON_SLOT_BASENAME_RE = /^[A-Za-z0-9_-]+$/;

/** True when `filename` is a safe drive-unit Python basename with no path segments. */
export function isTrustedSlotDeviceFilename(filename: string): boolean {
  const trimmed = filename.trim();
  if (!trimmed) {
    return false;
  }

  if (trimmed.includes('/') || trimmed.includes('\\') || trimmed.includes('..')) {
    return false;
  }

  if (trimmed.toLowerCase() === EMPTY_SLOT_FILENAME) {
    return true;
  }

  if (!trimmed.endsWith('.py')) {
    return false;
  }

  const baseName = trimmed.slice(0, -3);
  return baseName.length > 0 && SAFE_PYTHON_SLOT_BASENAME_RE.test(baseName);
}

/**
 * Normalize a device-reported slot filename before storing it in Redux.
 * Returns `empty.py` for empty slots, a trusted basename for occupied slots, or `null` when rejected.
 */
export function sanitizeDeviceReportedSlotFilename(filename: string | null | undefined): string | null {
  if (filename == null) {
    return null;
  }

  const trimmed = filename.trim();
  if (trimmed === '' || trimmed.toLowerCase() === EMPTY_SLOT_FILENAME) {
    return EMPTY_SLOT_FILENAME;
  }

  return isTrustedSlotDeviceFilename(trimmed) ? trimmed : null;
}

function sanitizeProgramTitleFilename(programTitle: string, fallbackSlot: string): string {
  const baseTitle = programTitle.replace(/\.[^/.]+$/, '');
  const safeBaseName = baseTitle.replace(/[^A-Za-z0-9_-]/g, '_') || fallbackSlot;
  return `${safeBaseName}.py`;
}

/**
 * Resolve the Python filename for a drive-unit slot from cached metadata.
 * Untrusted `deviceFilename` values fall back to a sanitized project title or slot name.
 */
export function normalizeSlotFilename(slotInfo: SlotFilenameSlotInfo | null | undefined, fallbackSlot: string): string {
  const deviceFilename = typeof slotInfo?.deviceFilename === 'string' ? slotInfo.deviceFilename.trim() : '';
  if (deviceFilename && isTrustedSlotDeviceFilename(deviceFilename)) {
    return deviceFilename.toLowerCase() === EMPTY_SLOT_FILENAME ? EMPTY_SLOT_FILENAME : deviceFilename;
  }

  const programTitle = typeof slotInfo?.programTitle === 'string' ? slotInfo.programTitle.trim() : '';
  if (programTitle) {
    return sanitizeProgramTitleFilename(programTitle, fallbackSlot);
  }

  return sanitizeProgramTitleFilename('', fallbackSlot);
}
